import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '../ui/shadcn/button'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/shadcn/popover'
import { Calendar } from '../ui/shadcn/calendar'
import { cn } from '@/lib/utils'
import { format } from 'date-fns'
import { CalendarIcon } from '../ui/icons/icons-new'

interface Props {
  onClick?: (date: Date) => void
  disabled?: boolean
  isScheduling?: boolean
  newButton?: boolean
}

export const PostPreviewScheduleButton = ({
  onClick,
  disabled,
  isScheduling,
  newButton,
}: Props) => {
  const [open, setOpen] = useState(false)
  const [date, setDate] = useState<Date>()
  const [time, setTime] = useState<string>(format(new Date(), 'HH:mm'))

  const datetime = useMemo(
    () => (date && time ? new Date(`${date.toDateString()} ${time}`) : undefined),
    [date, time],
  )

  const handleSchedule = () => {
    if (!datetime) {
      toast.error('Please select a date and time')
      return
    }

    onClick?.(datetime)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {newButton ? (
          <Button variant="secondary" disabled={disabled}>
            <CalendarIcon color="var(--foreground)" />
            {isScheduling ? 'Scheduling...' : 'Schedule'}
          </Button>
        ) : (
          <Button
            variant="secondary"
            disabled={disabled}
            className="flex items-center gap-1.5 text-white">
            <CalendarIcon />
            {isScheduling ? 'Scheduling...' : 'Schedule'}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="dark:bg-card w-auto rounded-lg bg-slate-200 p-0" align="end">
        <div>
          <Calendar
            className="rounded-lg"
            mode="single"
            selected={date}
            onSelect={setDate}
            autoFocus
            disabled={(d) => {
              const today = new Date()
              today.setHours(0, 0, 0, 0)

              return d < today
            }}
            weekStartsOn={1}
          />
          <div className="space-y-2 pt-2">
            <input
              type="time"
              value={time || ''}
              onChange={(e) => setTime(e.target.value)}
              className={cn(
                'dark:bg-background dark:text-foreground w-full rounded bg-white px-2 text-center text-black',
                '[&::-webkit-datetime-edit-fields-wrapper]:p-2',
                '[&::-webkit-datetime-edit-text]:px-1',
              )}
            />
            {datetime && (
              <p className="dark:text-muted-foreground text-center text-xs text-gray-600">
                {format(datetime, 'PPPp')}
              </p>
            )}
            <Button onClick={handleSchedule} disabled={!datetime} className="w-full" size="sm">
              Schedule Post
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
